// components/ProtectedRoute.js

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCurrentUser,
  resolvePostLoginPath,
  userKeyFromClaims,
  getIdTokenClaims,
  hasSubmittedForApproval,
  hasCompletedOnboarding,
} from '../common/auth';
import { fetchTenantAccessState, syncOnboardingFlagsFromTenant } from '../common/tenantAccess';

const PENDING_PATH = '/solicitud-pendiente';
const HOME_PATH = '/Home';

const ProtectedRoute = ({ children, allowPendingApproval = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const finish = () => {
      if (!cancelled) setChecking(false);
    };

    const redirect = (path) => {
      if (!path || path === location.pathname) {
        finish();
        return;
      }
      navigate(path, { replace: true });
    };

    const user = getCurrentUser();
    if (!user) {
      redirect('/login');
      return undefined;
    }

    user.getSession(async (err, session) => {
      if (cancelled) return;
      if (err || !session?.isValid()) {
        redirect('/login');
        return;
      }

      try {
        const state = await fetchTenantAccessState();
        if (cancelled) return;

        const userKey = userKeyFromClaims(await getIdTokenClaims().catch(() => ({})));
        if (state.tenant) {
          syncOnboardingFlagsFromTenant(state.tenant, userKey);
        }

        const awaitingApproval =
          state.pending ||
          (hasSubmittedForApproval(userKey) && !hasCompletedOnboarding(userKey));

        if (state.approved) {
          if (allowPendingApproval) {
            redirect(HOME_PATH);
            return;
          }
          finish();
          return;
        }

        if (awaitingApproval) {
          if (allowPendingApproval) {
            finish();
            return;
          }
          redirect(PENDING_PATH);
          return;
        }

        if (allowPendingApproval) {
          const next = await resolvePostLoginPath();
          if (cancelled) return;
          redirect(next);
          return;
        }

        const next = await resolvePostLoginPath();
        if (cancelled) return;
        if (next === HOME_PATH) {
          finish();
          return;
        }
        redirect(next);
      } catch {
        if (!cancelled) redirect('/login');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [navigate, allowPendingApproval, location.pathname]);

  if (checking) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Verificando sesión…</div>;
  }

  return children;
};

export default ProtectedRoute;
